import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ContractInterface, IdInterface, OperationInterface, OrderType, ProductInterface, SelectedProductInterface } from '../data';
import { OperationFactoryInterface } from '../factories/operation.factory.interface';
import { OrderApiRepository } from '../repositories/order-api.repository';
import { Transaction } from '../transaction/transaction';

@Injectable()
export class OperationProvider {
    private operation: OperationInterface;
    private typesSort = [
        OrderType.SUBSCRIPTION,
        OrderType.SUBSCRIPTION_PERIOD,
        OrderType.REFUND,
        OrderType.REFUND_PERIOD,
        OrderType.INTERNAL_TRANSFER,
        OrderType.EXTERNAL_TRANSFER,
    ];

    constructor(private operationFactory: OperationFactoryInterface, private orderApiRepository: OrderApiRepository) {}

    find(): Observable<Transaction<OperationInterface>> {
        if (!this.operation) {
            return of(Transaction.error());
        }

        return of(Transaction.success(this.operation));
    }

    create(
        contract: ContractInterface,
        product: ProductInterface,
        selectedProduct: SelectedProductInterface,
        onlyRefund = false,
        editing = false,
        readonly = false,
        clientCode?: IdInterface
    ): Observable<Transaction<void>> {
        return of(true).pipe(
            switchMap((_) => {
                this.operation = this.operationFactory.build();
                return this.fillOperation(contract, product, selectedProduct, onlyRefund, editing, readonly, clientCode);
            })
        );
    }

    private fillOperation(
        contract: ContractInterface,
        product: ProductInterface,
        selectedProduct: SelectedProductInterface,
        onlyRefund = false,
        editing = false,
        readonly = false,
        clientCode?: IdInterface
    ): Observable<Transaction<void>> {
        return this.orderApiRepository.types(contract.encryptedCode, selectedProduct, clientCode).pipe(
            map((transaction) =>
                transaction.map(
                    (types) => {
                        this.operation.contract = contract;
                        this.operation.product = product;
                        this.operation.selectedProduct = selectedProduct;
                        this.operation.types = types.sort((a, b) => {
                            if (this.typesSort.indexOf(a.type) < this.typesSort.indexOf(b.type)) {
                                return -1;
                            }

                            if (this.typesSort.indexOf(a.type) > this.typesSort.indexOf(b.type)) {
                                return 1;
                            }

                            return 0;
                        });
                        this.operation.onlyRefund = onlyRefund;
                        this.operation.editing = editing;
                        this.operation.readonly = readonly;

                        return Transaction.success();
                    },
                    (throwable) => Transaction.error(throwable.message)
                )
            )
        );
    }

    update(
        contract: ContractInterface,
        product: ProductInterface,
        selectedProduct: SelectedProductInterface,
        onlyRefund = false,
        editing = false,
        readonly = false,
        clientCode?: IdInterface
    ): Observable<Transaction<void>> {
        return this.fillOperation(contract, product, selectedProduct, onlyRefund, editing, readonly, clientCode);
    }
}
